import { FC, useMemo, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import clsx from 'clsx';
import type { ChartData, ChartOptions, Plugin, TooltipItem } from 'chart.js';
import {
  getCartesianChartOptions,
  lineChartVerticalCrosshairPlugin,
  resolveDatasetColor,
} from './chartConfig';
import { createChartRenderCompletePlugin } from './createChartRenderCompletePlugin';
import type { BaseChartProps } from './types';

interface LineChartProps extends BaseChartProps {
  yAxisPosition?: 'left' | 'right';
  fill?: boolean;
}

const LineChart: FC<LineChartProps> = ({
  labels,
  datasets,
  colors,
  height = 168,
  className,
  onRendered,
  disableAnimation = false,
  yAxisPosition,
  fill = false,
  tooltipTitles,
}) => {
  const onRenderedRef = useRef(onRendered);
  onRenderedRef.current = onRendered;

  const chartData = useMemo<ChartData<'line'>>(() => {
    const chartDatasets = datasets.map((ds, index) => {
      const color = resolveDatasetColor(index, ds.color, colors);
      return {
        label: ds.label,
        data: ds.data,
        borderColor: color,
        backgroundColor: fill ? `${color}20` : color,
        fill,
        borderWidth: 2,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHitRadius: 8,
        pointBackgroundColor: color,
        pointBorderColor: color,
        pointBorderWidth: 0,
      };
    });
    return { labels, datasets: chartDatasets };
  }, [labels, datasets, colors, fill]);

  const options = useMemo<ChartOptions<'line'>>(() => {
    const base = getCartesianChartOptions({
      showGridX: false,
      showGridY: true,
    });
    return {
      ...base,
      ...(disableAnimation ? { animation: false as const } : {}),
      scales: {
        ...base.scales,
        ...(yAxisPosition
          ? { y: { ...base.scales?.y, position: yAxisPosition } }
          : {}),
      },
      plugins: {
        ...base.plugins,
        tooltip: {
          ...base.plugins?.tooltip,
          callbacks: {
            ...base.plugins?.tooltip?.callbacks,
            title: (items: TooltipItem<'line'>[]) => {
              const index = items[0]?.dataIndex;
              if (index !== undefined && tooltipTitles?.[index] !== undefined) {
                return tooltipTitles[index];
              }
              const label = items[0]?.label;
              return typeof label === 'string' ? label : '';
            },
          },
        },
      },
      interaction: {
        mode: 'index',
        intersect: false,
      },
      hover: {
        mode: 'index',
        intersect: false,
      },
    };
  }, [disableAnimation, yAxisPosition, tooltipTitles]);

  const linePlugins = useMemo((): Plugin<'line'>[] => {
    const plugins: Plugin<'line'>[] = [lineChartVerticalCrosshairPlugin];

    if (onRendered) {
      plugins.push(
        createChartRenderCompletePlugin<'line'>(() => {
          onRenderedRef.current?.();
        }),
      );
    }

    return plugins;
  }, [onRendered]);

  return (
    <div
      className={clsx('tw-w-full', className)}
      style={{ height: typeof height === 'number' ? `${height}px` : height }}
      data-cy="analytics-chart-line"
    >
      <Line data={chartData} options={options} plugins={linePlugins} />
    </div>
  );
};

export default LineChart;
